const HARD_NAVIGATION_FALLBACK_MS = 8_000;

/**
 * `navigate` callback for `signUp.finalize()` / `signIn.finalize()`.
 * Clerk invokes it inside `setActive`, before emitting the session and
 * running `@clerk/nextjs`'s `router.refresh()` on auth-state change.
 *
 * Relative URLs: push through the App Router (Chrome and non-ITP paths).
 *
 * Absolute URLs: Clerk's Safari ITP `decorateUrl` returns a FAPI touch
 * URL. Assign `location.href` and return a pending promise so Clerk stays
 * in its transitive state until the document navigates away. Without the
 * hold, WebKit schedules the hard navigation asynchronously, Clerk
 * continues, RSC fetches race, and signed-in `/signup` middleware can
 * redirect to `/home` instead of `/onboarding`.
 *
 * Docs:
 *   https://clerk.com/docs/guides/development/custom-flows/authentication/email-sms-otp
 */
export function createClerkFinalizeNavigate(
  router: { push: (url: string) => void },
  redirectToPath: string
): (params: {
  decorateUrl: (url: string) => string;
  session?: unknown;
}) => void | Promise<void> {
  return ({ decorateUrl }) => {
    const url = decorateUrl(redirectToPath);
    if (!url.startsWith('http')) {
      router.push(url);
      return;
    }

    window.location.href = url;
    return new Promise<void>(() => {
      window.setTimeout(() => {
        // Hard-nav fallback: same hold as the primary path so Clerk does not
        // run router.refresh() while navigation is still in flight.
        window.location.href = redirectToPath;
      }, HARD_NAVIGATION_FALLBACK_MS);
    });
  };
}
